/**
 * The yellow cross (spec 02 §7.5; D_A653, the main loop's $0A3B, D_1AB6, render-screen.s L_198A..L_1A5F):
 * 2..5 strikes, one at a time. Each starts with the border flashing and play paused, taking the
 * software slots 0 and 1 - where the Barons live, so they are gone - then moves one column left and
 * one row down per main-loop pass from row 2 to row $16, killing every monster in its 48 x 48 box, in
 * a bubble or out, and on every step but the last maybe one monster anywhere (rand < 6 picks a slot).
 */

import { killMonsterBy } from '../bubbles';
import { rngOf } from '../random';
import { announce, pause } from './act';
import {
  STRIKE_BOX, STRIKE_BOX_X, STRIKE_BOX_Y, STRIKE_COL, STRIKE_COL_MASK, STRIKE_FLASH_FRAMES, STRIKE_LAST_ROW,
  STRIKE_RANDOM_KILL, STRIKE_ROW, STRIKE_VANISH_PASSES, STRIKES_MASK, STRIKES_MIN,
} from './tuning';
import type { ItemSystem } from './state';
import type { Monster, PlayerId, World } from '../types';

/** D_A653: the cross taken - D_58FF = 2 + rand & 3 strikes to come. */
export function takeYellowCross(world: World, sys: ItemSystem, by: PlayerId): void {
  sys.strike.passes = STRIKES_MIN + (rngOf(world).int(256) & STRIKES_MASK);
  sys.strike.by = by;
}

/** The strike's kill box, top-left in the VIC frame (L_19CF: column x 8 + $0C, row x 8 + $0D). */
export function strikeBox(sys: ItemSystem): { x: number; y: number } {
  const s = sys.strike;
  return { x: (s.col * 8 + STRIKE_BOX_X) & 0xff, y: (s.row * 8 + STRIKE_BOX_Y) & 0xff };
}

/** A monster the C64 has in states 1..$0A: out of any bubble, alive, not frozen by the heart ($16/$17). */
function isOut(world: World, m: Monster): boolean {
  if (m.state === 'dead' || m.state === 'trapped') return false;
  return !(m.frozen && world.power.heart >= 0);
}

/** 8-bit `sbc / bcc / cmp #$30 / bcs`: v from the box's edge, within 48. */
const within = (v: number, edge: number): boolean => v >= edge && v - edge < STRIKE_BOX;

/** L_19E7 (bubbles holding a monster, at the bubble's X, Y) and L_1A2E (monsters out) inside the box. */
function strikeBoxKills(world: World, sys: ItemSystem): void {
  const box = strikeBox(sys);
  for (const m of world.monsters.slice()) {
    let x = m.x;
    let y = m.y;
    if (m.state === 'trapped') {
      // Carried by water ($0D) it is in neither loop.
      const b = world.bubbles.find((bb) => bb.id === m.bubbleId);
      if (!b) continue;
      x = b.x;
      y = b.y;
    } else if (!isOut(world, m)) continue;
    if (within(x & 0xff, box.x) && within(y & 0xff, box.y)) killMonsterBy(world, m.id, 'other', sys.strike.by);
  }
}

/** L_198A..L_1A5F: one pass of the strike on screen. */
function stepStrike(world: World, sys: ItemSystem): void {
  const s = sys.strike;
  if (s.vanish >= 0) {
    // $38 (turned to $36 on the same pass), $36 (freed), then L_19C1 ends it and counts the pass.
    if (--s.vanish <= 0) {
      s.on = false;
      if (s.passes > 0) s.passes--;
    }
    return;
  }
  s.col--;
  s.row++;
  strikeBoxKills(world, sys);
  if (s.row === STRIKE_LAST_ROW) {
    s.vanish = STRIKE_VANISH_PASSES;
    return;
  }
  const r = rngOf(world).int(256);
  if (r >= STRIKE_RANDOM_KILL) return;
  const m = world.monsters[r];
  if (m && isOut(world, m)) killMonsterBy(world, m.id, 'other', s.by);
}

/** D_1AB6: a new strike - the Barons' slots taken, the border flashing, a random column. */
function beginStrike(world: World, sys: ItemSystem): void {
  const s = sys.strike;
  world.barons.length = 0;
  s.on = true;
  s.vanish = -1;
  s.row = STRIKE_ROW;
  s.col = STRIKE_COL + (rngOf(world).int(256) & STRIKE_COL_MASK);
  pause(world, sys, STRIKE_FLASH_FRAMES);
  announce(world, 'strike');
}

/** L_198A (at the pass's start: the strike steps) ... $0A3B (later: a new one if due and none is on). */
export function strikeStep(world: World, sys: ItemSystem): void {
  if (sys.strike.on) stepStrike(world, sys);
}

export function strikeBegin(world: World, sys: ItemSystem): void {
  if (sys.strike.passes > 0 && !sys.strike.on) beginStrike(world, sys);
}

/** The Baron came ($1621: D_58FF = 0, and its slots overwrite the strike's): no more strikes. */
export function cancelStrikes(sys: ItemSystem): void {
  sys.strike.passes = 0;
  sys.strike.on = false;
  sys.strike.vanish = -1;
}
